import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Target, Users, Award } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const About = () => {
  const features = [
    {
      icon: Target,
      title: "Customized Solutions",
      description: "Tailored IT strategies that align with your specific business needs and drive meaningful results.",
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Our professionals possess deep knowledge across various industries and technologies.",
    },
    {
      icon: Award,
      title: "Proven Track Record",
      description: "Demonstrated ability to deliver exceptional results across various technology initiatives.",
    },
  ];

  return (
    <section id="about" className="py-16 md:py-24 lg:py-32 bg-background">
      <div className="container">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16 space-y-4">
          <div className="inline-block">
            <span className="text-sm font-medium text-primary bg-primary/10 px-4 py-2 rounded-full">
              About Us
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold">
            About Our{" "}
            <span className="text-primary">Company</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Oethical Technology is dedicated to delivering innovative and effective IT solutions
            tailored to the unique needs of our clients. Our team of experts is committed to
            driving technological success for businesses of all sizes and industries.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Image */}
          <div className="relative order-2 lg:order-1">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl blur-3xl" />
            <div className="relative rounded-3xl overflow-hidden border border-border shadow-2xl">
              <img
                src={process.env.PUBLIC_URL + "/assets/img/about-portrait.jpg"}
                alt="About Oethical Technology"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
          </div>

          {/* Content */}
          <div className="space-y-6 order-1 lg:order-2">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                Our Commitment to Innovation and Excellence
              </h3>
              <p className="text-muted-foreground text-lg">
                We believe in crafting customized IT strategies that align with your specific
                business needs and drive meaningful results.
              </p>
            </div>

            <Tabs defaultValue="approach" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="approach">Approach</TabsTrigger>
                <TabsTrigger value="expertise">Expertise</TabsTrigger>
                <TabsTrigger value="values">Values</TabsTrigger>
              </TabsList>

              <TabsContent value="approach" className="space-y-4 mt-6">
                <p className="text-muted-foreground italic">
                  At Oethical Technology, our approach is centered around understanding the unique
                  challenges and goals of each client.
                </p>
                <div className="space-y-4">
                  {[
                    {
                      title: "Customized Solutions for Diverse Needs",
                      description:
                        "We tailor our IT solutions to fit the unique requirements of each business, ensuring effectiveness and efficiency.",
                    },
                    {
                      title: "Innovative Technologies and Strategies",
                      description:
                        "Our team stays on the cutting edge of technological advancements to provide innovative solutions.",
                    },
                    {
                      title: "Ongoing Support and Partnership",
                      description:
                        "We are committed to being a long-term partner, offering continuous support throughout the lifecycle.",
                    },
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">{item.title}</h4>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="expertise" className="space-y-4 mt-6">
                <p className="text-muted-foreground italic">
                  Our team of experts brings extensive experience and knowledge across various IT
                  domains.
                </p>
                <div className="space-y-4">
                  {[
                    {
                      title: "Deep Industry Knowledge",
                      description:
                        "Our professionals possess deep knowledge in a range of industries, allowing us to offer relevant insights.",
                    },
                    {
                      title: "Proven Track Record",
                      description:
                        "With a proven track record of successful projects and satisfied clients, we deliver exceptional results.",
                    },
                    {
                      title: "Expertise in Emerging Technologies",
                      description:
                        "We are adept at harnessing emerging technologies to provide innovative solutions for modern businesses.",
                    },
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">{item.title}</h4>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="values" className="space-y-4 mt-6">
                <p className="text-muted-foreground italic">
                  Our core values are the foundation of our business practices and guide our
                  interactions with clients.
                </p>
                <div className="space-y-4">
                  {[
                    {
                      title: "Integrity and Transparency",
                      description:
                        "We uphold the highest standards of integrity and transparency, ensuring clients are informed and involved.",
                    },
                    {
                      title: "Commitment to Excellence",
                      description:
                        "Our commitment to excellence drives us to continually improve and deliver superior solutions.",
                    },
                    {
                      title: "Client-Centric Focus",
                      description:
                        "We place our clients at the center of everything we do, ensuring solutions meet their needs.",
                    },
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <div>
                        <h4 className="font-semibold mb-1">{item.title}</h4>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <Button asChild size="lg" className="mt-6">
              <Link to="/aboutOethical">Learn More</Link>
            </Button>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-6 mt-16">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card key={index} className="border-border hover:border-primary/50 transition-colors">
                <CardHeader>
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle>{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
